import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Ticket } from 'lucide-react';
import Footer from '../components/Footer';
import RsvpForm from '../components/RsvpForm';
import ShareEvent from '../components/ShareEvent';
import { getEvent } from '../lib/api';
import { fmtTimeRange, fmtLongDate, isPast, priceSummary } from '../lib/events';
import { IMAGES } from '../mock';

function Meta({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <li className="flex items-start gap-4">
      <span className="w-11 h-11 rounded-full bg-[#eef4f8] flex items-center justify-center shrink-0"><Icon size={19} className="text-[#4a9cd4]" /></span>
      <div>
        <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#999] uppercase">{label}</p>
        <p className="font-sans-ah text-[15px] text-[#3a3a3a]">{value}</p>
      </div>
    </li>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); getEvent(id).then(setE).catch(() => setNotFound(true)); }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f6f2] px-6">
        <p className="font-serif-ah text-[32px] text-[#555] mb-4">Event not found.</p>
        <Link to="/events" className="btn-ah-solid font-sans-ah text-[13px] px-8 py-3.5 rounded-sm">BACK TO EVENTS</Link>
      </div>
    );
  }
  if (!e) return <div className="min-h-screen bg-[#f8f6f2] animate-pulse" />;
  const past = isPast(e.date);

  return (
    <>
      <section className="relative h-[52vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${e.image || IMAGES.heroA})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1f2c33] via-[#1f2c33]/40 to-transparent" />
        <div className="relative z-10 max-w-[1240px] mx-auto px-6 lg:px-10 w-full pb-12">
          <span className="inline-block bg-[#4a9cd4] text-white font-sans-ah text-[10px] tracking-wide-ah px-3 py-1.5 rounded-sm mb-4">{past ? 'PAST EVENT' : 'EVENT'}</span>
          <h1 data-testid="event-title" className="font-serif-ah text-white text-[38px] md:text-[54px] leading-tight">{e.title}</h1>
        </div>
      </section>
      <section className="bg-[#f8f6f2] py-16 md:py-20">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Link to="/events" className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4] mb-8"><ArrowLeft size={15} /> ALL EVENTS</Link>
            <ul className="space-y-6 mb-10" data-testid="event-meta">
              <Meta icon={Calendar} label="Date" value={fmtLongDate(e.date)} />
              <Meta icon={Clock} label="Time" value={fmtTimeRange(e)} />
              <Meta icon={MapPin} label="Location" value={e.location} />
              <Meta icon={Ticket} label="Tickets" value={priceSummary(e)} />
            </ul>
            {!past && <ShareEvent eventId={e.id} />}
            <div className="font-sans-ah text-[16px] text-[#454545] font-300 leading-[1.9] whitespace-pre-line">{e.description}</div>
          </div>
          <div className="lg:col-span-3 bg-white rounded-sm p-8 md:p-10 shadow-[0_10px_50px_rgba(0,0,0,0.07)] border border-neutral-100 self-start">
            {past ? (
              <p data-testid="rsvp-closed" className="font-serif-ah text-[24px] text-[#555] text-center py-10">This event has already taken place. RSVPs are closed.</p>
            ) : <RsvpForm eventId={e.id} eventTitle={e.title} event={e} />}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
